import { useContext, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "../../../context/auth/AuthContext";
import Icon from "../Icon/Icon";
import LanguageSwitch from "../LanguageSwitch/LanguageSwitch";
import SidePanel from "../SidePanel/SidePanel";
import "./PublicPage.css";

export interface PublicPageProps {
  /** sm 440px, md 560px (default, the widest a page gets) */
  width?: "sm" | "md";
  /** page title, shown inside the card */
  title?: ReactNode;
  /** grey text under the title */
  description?: ReactNode;
  /** small grey text under the card */
  footer?: ReactNode;
  /** hide the EN / ES switch */
  hideLanguage?: boolean;
  children?: ReactNode;
}

/**
 * The dark side panel on its own (logo), for flows that
 * keep their own page body, e.g. the guarantor onboarding steps. Put it
 * and the page body side by side in a `ds-public-page` row.
 */
export const PublicPageSide = ({
  hideLanguage = false,
}: {
  hideLanguage?: boolean;
}) => {
  const { i18n } = useTranslation();
  const auth = useContext(AuthContext) as any;
  const [isLangChanging, setIsLangChanging] = useState(false);

  const changeLanguage = async (lang: string) => {
    if (isLangChanging) return;
    setIsLangChanging(true);
    try {
      await i18n.changeLanguage(lang);
      localStorage.setItem("i18nextLng", lang);
      // saves the choice for a signed-in user; does nothing otherwise
      await auth?.changeUserLng?.({ skipUserLoad: true });
    } catch {
      // the language already changed on screen; saving it can fail quietly
    } finally {
      setIsLangChanging(false);
    }
  };

  return (
    <>
      <SidePanel className="ds-public-page__side" />
      {!hideLanguage && (
        <div className="ds-public-page__lang">
          <LanguageSwitch
            currentLang={i18n.language || "es"}
            isLangChanging={isLangChanging}
            onChange={changeLanguage}
          />
        </div>
      )}
    </>
  );
};

/**
 * Frame for pages that open from a link, outside the app: the dark side
 * panel (like the login pages), the language switch top right and the
 * content centred on the page (no card).
 * Example: <PublicPage width="sm" title="Forgot password">…</PublicPage>
 */
const PublicPage = ({
  width = "md",
  title,
  description,
  footer,
  hideLanguage = false,
  children,
}: PublicPageProps) => {
  return (
    <div className="ds-public-page">
      <PublicPageSide hideLanguage={hideLanguage} />
      <main className="ds-public-page__main">
        <div
          className={`ds-public-page__column ds-public-page__column--${width}`}
        >
          <div className="ds-public-page__body">
            {(title || description) && (
              <div className="ds-public-page__head">
                {title && <h1 className="ds-public-page__title">{title}</h1>}
                {description && (
                  <p className="ds-public-page__description">{description}</p>
                )}
              </div>
            )}
            {children}
          </div>
          {footer && <p className="ds-public-page__footer">{footer}</p>}
        </div>
      </main>
    </div>
  );
};

export interface PublicPageStatusProps {
  /** loading = spinner, success = green tick, danger = red warning */
  tone?: "loading" | "success" | "danger";
  title: ReactNode;
  description?: ReactNode;
  /** e.g. a Button */
  children?: ReactNode;
}

/**
 * Centred message inside PublicPage: icon, title, text, optional button.
 * Example: <PublicPageStatus tone="success" title="Payment done" />
 */
export const PublicPageStatus = ({
  tone = "loading",
  title,
  description,
  children,
}: PublicPageStatusProps) => (
  <div
    className="ds-public-page__status"
    role={tone === "loading" ? "status" : undefined}
  >
    {tone === "loading" ? (
      <span className="ds-public-page__spinner" aria-hidden="true" />
    ) : (
      <span
        className={`ds-public-page__status-icon ds-public-page__status-icon--${tone}`}
      >
        <Icon name={tone === "success" ? "checkCircle" : "warning"} size={28} />
      </span>
    )}
    <div className="ds-public-page__head">
      <h1 className="ds-public-page__title">{title}</h1>
      {description && (
        <p className="ds-public-page__description">{description}</p>
      )}
    </div>
    {children}
  </div>
);

export default PublicPage;
